/**
 * Dashboard Config Hooks
 * Centralised hooks covering roles, metabase dashboards, details, and full CRUD.
 *
 * Includes normalisation helpers for legacy single-role and new multi-role records.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/config/logger';
import { getUserRoles } from '@/services/rolesApi';
import { getResourcesFlat, getDashboardDetails } from '@/services/metabaseApi';
import {
  createDashboardItem,
  updateDashboardItem,
  deleteDashboardItem,
  getDashboardItems,
  getDashboardItem,
} from '@/services/dashboardConfigApi';
import type {
  ReferenceUserRole,
  MetabaseFlatResource,
  MetabaseDashboardDetails,
  DashboardItem,
  NormalisedDashboardItem,
  CreateDashboardItemRequest,
  UpdateDashboardItemRequest,
  MutateDashboardItemResponse,
} from '@/types/dashboardConfig';

// ─── Normalisation helpers ────────────────────────────────────────────────────

/**
 * Parse a value that may be a JSON string, array, or null.
 * Returns an empty array on failure.
 */
export function parseJsonArray<T>(val: unknown): T[] {
  if (Array.isArray(val)) return val as T[];
  if (typeof val === 'string' && val.trim()) {
    // Try JSON array first
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed as T[];
    } catch {
      // not JSON — fall through
    }
    // Comma-separated string (e.g. "CS,ES" or "1,2")
    return val
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => {
        const n = Number(s);
        return (isNaN(n) ? s : n) as unknown as T;
      });
  }
  return [];
}

/**
 * Normalise a raw DashboardItem to a stable shape for UI consumption.
 * Handles both old single-role records (userType) and new multi-role (userTypes).
 */
export function normaliseDashboardItem(raw: DashboardItem): NormalisedDashboardItem {
  // userTypes: prefer array field, fall back to JSON string, fall back to single userType
  let userTypes: string[] = parseJsonArray<string>(raw.userTypes);
  if (userTypes.length === 0 && raw.userType) {
    userTypes = [raw.userType];
  }

  const userRoleIds: number[] = parseJsonArray<number>(raw.userRoleIds);

  return {
    id: raw.id,
    customName: raw.customName ?? '',
    metabaseQuestionName: raw.metabaseQuestionName ?? '',
    resourceType: raw.resourceType ?? 'dashboard',
    resourceId: raw.resourceId,
    userTypes,
    userRoleIds,
    isDefault: raw.isDefault ?? false,
    dashboardQuestions: parseJsonArray(raw.dashboardQuestions),
    dashboardFilters: parseJsonArray(raw.dashboardFilters),
    createdAt: raw.createdAt ?? '',
    createdBy: raw.createdBy,
    updatedDate: raw.updatedDate ?? null,
  };
}

// ─── useUserRoles ─────────────────────────────────────────────────────────────

export interface UseUserRolesResult {
  roles: ReferenceUserRole[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useUserRoles(): UseUserRolesResult {
  const [roles, setRoles] = useState<ReferenceUserRole[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getUserRoles()
      .then((data) => { if (!cancelled) setRoles(data); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load roles'); })
      .finally(() => { if (!cancelled) setIsLoading(false); });

    return () => { cancelled = true; };
  }, [tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  return { roles, isLoading, error, refetch };
}

// ─── useMetabaseDashboards ────────────────────────────────────────────────────

export interface UseMetabaseDashboardsResult {
  dashboards: MetabaseFlatResource[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useMetabaseDashboards(
  loginUserId: string | number | null
): UseMetabaseDashboardsResult {
  const { token, user } = useAuth();
  // Fallback: if the prop is missing, use the logged-in user's id directly
  const resolvedUserId = loginUserId ?? user?.id ?? null;
  const [dashboards, setDashboards] = useState<MetabaseFlatResource[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!resolvedUserId || !token) {
      logger.warn('[useMetabaseDashboards] Skipping fetch – missing resolvedUserId or token', { resolvedUserId, hasToken: !!token });
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    logger.log('[useMetabaseDashboards] Fetching metabase resources for userId:', resolvedUserId);
    getResourcesFlat(resolvedUserId, token)
      .then((all) => {
        if (!cancelled) {
          const filtered = all.filter((r) => r.resourceType === 'dashboard');
          logger.log(`[useMetabaseDashboards] Loaded ${all.length} resources, ${filtered.length} dashboards`);
          setDashboards(filtered);
        }
      })
      .catch((err) => {
        logger.error('[useMetabaseDashboards] Error fetching resources:', err);
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load dashboards');
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });

    return () => { cancelled = true; };
  }, [resolvedUserId, token, tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  return { dashboards, isLoading, error, refetch };
}

// ─── useDashboardDetails ──────────────────────────────────────────────────────

export interface UseDashboardDetailsResult {
  details: MetabaseDashboardDetails | null;
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

export function useDashboardDetails(
  loginUserId: string | number | null,
  dashboardId: number | null
): UseDashboardDetailsResult {
  const { token } = useAuth();
  const [details, setDetails] = useState<MetabaseDashboardDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!loginUserId || !dashboardId || !token) {
      setDetails(null);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);
    setDetails(null);

    getDashboardDetails(loginUserId, dashboardId, token, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setDetails(data); })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load dashboard details');
        }
      })
      .finally(() => { if (!controller.signal.aborted) setIsLoading(false); });

    return () => controller.abort();
  }, [loginUserId, dashboardId, token, tick]);

  const retry = useCallback(() => setTick((t) => t + 1), []);

  return { details, isLoading, error, retry };
}

// ─── useDashboardConfigs ──────────────────────────────────────────────────────

export interface UseDashboardConfigsResult {
  items: NormalisedDashboardItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useDashboardConfigs(
  loginUserId: string | number | null,
  userType?: string
): UseDashboardConfigsResult {
  const { token } = useAuth();
  const [items, setItems] = useState<NormalisedDashboardItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!loginUserId || !token) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getDashboardItems(loginUserId, token, userType)
      .then((raw) => { if (!cancelled) setItems(raw.map(normaliseDashboardItem)); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load configs'); })
      .finally(() => { if (!cancelled) setIsLoading(false); });

    return () => { cancelled = true; };
  }, [loginUserId, token, userType, tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  return { items, isLoading, error, refetch };
}

// ─── useCreateDashboardConfig ─────────────────────────────────────────────────

export interface UseCreateDashboardConfigResult {
  create: (payload: CreateDashboardItemRequest) => Promise<MutateDashboardItemResponse>;
  isLoading: boolean;
  error: string | null;
}

export function useCreateDashboardConfig(
  loginUserId: string | number | null
): UseCreateDashboardConfigResult {
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (payload: CreateDashboardItemRequest): Promise<MutateDashboardItemResponse> => {
      if (!loginUserId || !token) return { success: false, error: 'Not authenticated' };
      setIsLoading(true);
      setError(null);
      try {
        const result = await createDashboardItem(loginUserId, token, payload);
        if (!result.success) setError(result.error ?? 'Create failed');
        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setIsLoading(false);
      }
    },
    [loginUserId, token]
  );

  return { create, isLoading, error };
}

// ─── useUpdateDashboardConfig ─────────────────────────────────────────────────

export interface UseUpdateDashboardConfigResult {
  update: (itemId: number, payload: Omit<UpdateDashboardItemRequest, 'id'>) => Promise<MutateDashboardItemResponse>;
  isLoading: boolean;
  error: string | null;
}

export function useUpdateDashboardConfig(
  loginUserId: string | number | null
): UseUpdateDashboardConfigResult {
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(
    async (
      itemId: number,
      payload: Omit<UpdateDashboardItemRequest, 'id'>
    ): Promise<MutateDashboardItemResponse> => {
      if (!loginUserId || !token) return { success: false, error: 'Not authenticated' };
      setIsLoading(true);
      setError(null);
      try {
        const result = await updateDashboardItem(loginUserId, token, itemId, payload);
        if (!result.success) setError(result.error ?? 'Update failed');
        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setIsLoading(false);
      }
    },
    [loginUserId, token]
  );

  return { update, isLoading, error };
}

// ─── useDeleteDashboardConfig ─────────────────────────────────────────────────

export interface UseDeleteDashboardConfigResult {
  remove: (itemId: number) => Promise<MutateDashboardItemResponse>;
  isLoading: boolean;
  error: string | null;
}

export function useDeleteDashboardConfig(
  loginUserId: string | number | null
): UseDeleteDashboardConfigResult {
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = useCallback(
    async (itemId: number): Promise<MutateDashboardItemResponse> => {
      if (!loginUserId || !token) return { success: false, error: 'Not authenticated' };
      setIsLoading(true);
      setError(null);
      try {
        const result = await deleteDashboardItem(loginUserId, token, itemId);
        if (!result.success) setError(result.error ?? 'Delete failed');
        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setIsLoading(false);
      }
    },
    [loginUserId, token]
  );

  return { remove, isLoading, error };
}

// ─── useDashboardItemIframe ───────────────────────────────────────────────────
// Keep existing iframe hook for the viewer page

export interface UseDashboardItemIframeResult {
  iframeUrl: string | null;
  isLoading: boolean;
  error: string | null;
  load: (itemId: number, userEmail: string) => Promise<void>;
  clear: () => void;
}

export function useDashboardItemIframe(
  loginUserId: string | number | null
): UseDashboardItemIframeResult {
  const { token } = useAuth();
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const load = useCallback(
    async (itemId: number, userEmail: string) => {
      if (!loginUserId || !token) return;
      clearTimer();
      setIframeUrl(null);
      setIsLoading(true);
      setError(null);
      try {
        const { apiClient } = await import('@/services/core/apiClient');
        const endpoint = `/api/ngpep/${loginUserId}/dashboard-items/${itemId}/iframe?user_email=${encodeURIComponent(userEmail)}`;
        const response = await apiClient.get<{ iframeUrl: string; expires_at_epoch_ms?: number }>(
          endpoint,
          token
        );
        if (response.success && response.data?.iframeUrl) {
          setIframeUrl(response.data.iframeUrl);
          const expiresMs = response.data.expires_at_epoch_ms;
          if (expiresMs) {
            const delay = expiresMs - Date.now() - 60_000;
            if (delay > 0) {
              refreshTimerRef.current = setTimeout(() => load(itemId, userEmail), delay);
            }
          }
        } else {
          setError(response.error ?? 'Failed to get iframe URL');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load iframe');
      } finally {
        setIsLoading(false);
      }
    },
    [loginUserId, token, clearTimer]
  );

  const clear = useCallback(() => {
    clearTimer();
    setIframeUrl(null);
    setError(null);
  }, [clearTimer]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  return { iframeUrl, isLoading, error, load, clear };
}

// ─── Legacy aliases (backward compat) ────────────────────────────────────────

/** @deprecated Use useCreateDashboardConfig */
export const useCreateDashboardItem = useCreateDashboardConfig;

/** @deprecated Use useDashboardConfigs */
export const useDashboardItems = (
  loginUserId: string | number | null,
  userType?: string
) => useDashboardConfigs(loginUserId, userType);
